
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Card } from "@/components/ui/card";
import { ChevronRight, MapPin } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Helmet } from 'react-helmet-async';

const Locations = () => {
  // Use useMemo to prevent recreating this array on each render
  const locations = useMemo(() => [
    { name: "Søborg", href: "/klaverundervisning-soborg", description: "Hovedlokation med optimale faciliteter" },
    { name: "København", href: "/klaverundervisning-kobenhavn", description: "Central undervisning i København" },
    { name: "Online", href: "/klaverundervisning-online", description: "Fleksibel undervisning hvor end du er" },
    { name: "Gladsaxe", href: "/klaverundervisning-gladsaxe", description: "Tæt på Gladsaxevej og Søborg Hovedgade" },
    { name: "Lyngby", href: "/klaverundervisning-lyngby", description: "Let tilgængeligt med offentlig transport" },
    { name: "Gentofte", href: "/klaverundervisning-gentofte", description: "Rolige omgivelser i Gentofte" },
    { name: "Buddinge", href: "/klaverundervisning-buddinge", description: "God busforbindelse med bus 6A" },
    { name: "Emdrup", href: "/klaverundervisning-emdrup", description: "Nem adgang med buslinje 6A" },
    { name: "Vangede", href: "/klaverundervisning-vangede", description: "Let tilgængeligt for Vangede-området" },
    { name: "Dyssegård", href: "/klaverundervisning-dyssegard", description: "Kort transporttid fra Dyssegård" },
    { name: "Nordvest", href: "/klaverundervisning-nordvest", description: "God forbindelse fra Nordvest-kvarteret" },
    { name: "Nørrebro", href: "/klaverundervisning-norrebro", description: "God forbindelse fra Nørrebro med buslinje 6A" }
  ], []);

  // Memoize the location cards to prevent unnecessary re-renders
  const locationCards = useMemo(() => locations.map((location) => (
    <Card key={location.href} className="hover:shadow-lg transition-shadow">
      <Link 
        to={location.href} 
        className="block p-6"
      >
        <div className="flex items-center mb-4">
          <MapPin className="h-5 w-5 text-piano-gold mr-2" />
          <h3 className="text-xl font-serif font-semibold">
            {location.name === "Nørrebro" 
              ? "Nørrebro" 
              : location.name === "Online" 
                ? "Online" 
                : `${location.name}`}
          </h3>
        </div>
        <p className="text-gray-600 mb-4">{location.description}</p>
        <div className="flex items-center text-piano-gold">
          <span className="mr-1">Læs mere</span>
          <ChevronRight className="h-4 w-4" />
        </div>
      </Link>
    </Card>
  )), [locations]);

  return (
    <Layout>
      <div id="top"></div>
      <Helmet>
        <title>Klaverundervisning Lokationer | Pianist Oscar Gilbert</title>
        <meta name="description" content="Find klaverundervisning nær dig med pianist Oscar Gilbert. Professionel klaverundervisning for alle aldre og niveauer i hele København og omegn." />
        {/* Add structured data for better SEO */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "MusicSchool",
              "name": "Oscar Gilbert Klaverundervisning",
              "description": "Professionel klaverundervisning for alle aldre og niveauer i København og omegn",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Niels Finsens Alle 86",
                "addressLocality": "Søborg",
                "postalCode": "2860",
                "addressCountry": "DK"
              },
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": "55.7297216",
                "longitude": "12.5220869"
              },
              "telephone": "+4540817127",
              "url": "https://pianoundervisning.dk"
            }
          `}
        </script>
      </Helmet>
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-piano-black to-gray-900 text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-6">
              Lokationer
            </h1>
            <p className="text-xl text-gray-300">
              Find klaverundervisning i dit område
            </p>
          </div>
        </div>
      </section>

      {/* Location Cards - with memoized cards */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-serif font-bold mb-4">Klaverundervisning nær dig</h2>
            <p className="text-lg text-gray-600">
              Oscar Gilbert tilbyder klaverundervisning flere steder i København og omegn. 
              Vælg den lokation, der passer dig bedst.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {locationCards}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-100">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-serif font-bold mb-4">Klar til at starte din musikalske rejse?</h2>
            <p className="text-lg text-gray-600 mb-8">
              Book en gratis prøvetime, og oplev forskellen med professionel klaverundervisning fra Oscar Gilbert.
            </p>
            <Button asChild className="bg-piano-gold hover:bg-piano-gold/90 text-white">
              <Link to="/kontakt">Book gratis prøvetime</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

// Export the component wrapped in React.memo to prevent unnecessary re-renders
export default React.memo(Locations);
